import dayjs from "dayjs";
import { getRefListNameByID } from "@/util/common_function";

// Builds the Housing and Tracking grid's "Summary" sentence for one
// inmateTracking row. Legacy never stores this - it's the InmateTracking
// "Summary" virtual field (RecordStructureBookings.asp, Case "InmateTracking"),
// assembled out of smaller conditional virtual fields:
//
//   <what happened> at <date>. Transport by <staff>. Reason: <reason>.
//   Approved by <staff>. Expected back at <date>. Returned at <date>.
//
// Each piece only appears when its own field is filled in. Spacing is
// normalised to single spaces (legacy's raw pattern yields "12:33.Transport"
// and double spaces before "Reason:"), everything else follows the legacy
// patterns verbatim.

const text = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

const is = (value, expected) =>
  text(value).toLowerCase() === expected.toLowerCase();

const formatDateTime = (date, time) => {
  if (!text(date)) return "";
  const raw = `${text(date)} ${text(time)}`.trim();
  const parsed = dayjs(raw);
  if (!parsed.isValid()) return raw;
  return parsed.format(text(time) ? "MM/DD/YYYY HH:mm" : "MM/DD/YYYY");
};

// Legacy's Sorting_Date: the actual date once the move has happened
// (Complete / In Transit), the scheduled date while it hasn't (Pending /
// Cancelled). Rows with no status at all (the "Clear" housing entry is
// added without one) fall back to the actual date.
const getSortingDate = (row) => {
  if (is(row?.movementStatus, "Pending") || is(row?.movementStatus, "Cancelled")) {
    return formatDateTime(row?.scheduledDate, row?.scheduledTime);
  }
  return formatDateTime(row?.actualDate, row?.actualTime);
};

// Summary_Cell / Summary_Holding / Summary_Isolation. The React movement
// form keeps the Holding/Isolation cell in holdingCell for both types, so
// Isolation falls back to it.
const getHousing = (row) => {
  if (is(row?.inJailType, "Cell")) {
    return [row?.block, row?.cell, row?.bed].map(text).filter(Boolean).join(":");
  }
  if (is(row?.inJailType, "Holding")) {
    return `Holding (${text(row?.holdingCell)})`;
  }
  if (is(row?.inJailType, "Isolation")) {
    return `Isolation (${text(row?.isolationCell) || text(row?.holdingCell)})`;
  }
  return "";
};

const getHousingWithAgency = (row) => {
  const agency = text(row?.housingAgency);
  return `${getHousing(row)}${agency ? ` (${agency})` : ""}`;
};

const getLoan = (row, courtText) =>
  is(row?.loanType, "Court")
    ? `${courtText} (${text(row?.court)})`
    : `Loan to ${text(row?.loanDestination)}${text(row?.programName)}${text(
        row?.loanToFacility
      )} (${text(row?.loanType)})`;

const getWhatHappened = (row) => {
  const location = row?.inmateLocation;
  const from = text(row?.originalCurrentLocation)
    ? `from ${text(row?.originalCurrentLocation)}`
    : "";

  if (is(location, "RELEASED")) return "Inmate Released";
  if (is(location, "CLEARED")) return "Assigned housing cleared";

  if (is(location, "In Jail")) {
    const housing = getHousingWithAgency(row);
    if (is(row?.moveReason, "Receive Inmate")) {
      return `Received and placed in ${housing}`;
    }
    if (is(row?.moveReason, "Transport Cancelled")) {
      return `Transport cancelled; inmate placed in ${housing}`;
    }
    return ["Move", from, "to", housing].filter(Boolean).join(" ");
  }

  if (is(location, "Loan")) {
    return [getLoan(row, "Go to court"), from].filter(Boolean).join(" ");
  }
  if (is(location, "Transport")) {
    return `Transporting to ${text(row?.transportDestination)}`;
  }
  if (is(location, "Perm Housing Change") || is(location, "Permanent Housing Change")) {
    return `Transporting (Permanent Housing Change) to ${text(row?.transportDestination)}`;
  }
  if (is(location, "Trustee")) {
    return `Go to trustee assignment (${text(row?.trusteeAssignment)})`;
  }
  if (is(location, "Work Release")) {
    return `Work Release (${text(row?.workReleaseAssignment)})`;
  }
  return "";
};

const getStaffDescription = (staffID, fallback) =>
  (staffID ? getRefListNameByID("", staffID, true, true) : "") || text(fallback);

export const buildInmateTrackingSummary = (row) => {
  if (!row) return "";

  const sortingDate = getSortingDate(row);
  const head = [getWhatHappened(row), sortingDate ? `at ${sortingDate}` : ""]
    .filter(Boolean)
    .join(" ");

  const transportBy = getStaffDescription(
    row?.transportingOfficerStaffID,
    row?.transportingOfficer
  );
  const approvedBy = getStaffDescription(
    row?.approvingOfficerStaffID,
    row?.approvingOfficer
  );
  const expectedBack = formatDateTime(row?.expectedBackDate, row?.expectedBackTime);
  // The API field is returnDate/returnTime; rows edited in this session
  // carry the form's returnBackDate/returnBackTime instead.
  const returned = formatDateTime(
    row?.returnDate ?? row?.returnBackDate,
    row?.returnTime ?? row?.returnBackTime
  );

  return [
    head ? `${head}.` : "",
    transportBy ? `Transport by ${transportBy}.` : "",
    text(row?.moveReason) ? `Reason: ${text(row?.moveReason)}.` : "",
    approvedBy ? `Approved by ${approvedBy}.` : "",
    expectedBack ? `Expected back at ${expectedBack}.` : "",
    returned ? `Returned at ${returned}.` : "",
  ]
    .filter(Boolean)
    .join(" ");
};

// Legacy's shorter Summary_Destination virtual field - the Movement Log's
// "Moved To" column.
export const buildInmateTrackingDestination = (row) => {
  if (!row) return "";
  const location = row?.inmateLocation;
  if (is(location, "Loan")) return getLoan(row, "Court");
  if (is(location, "Trustee")) return `Trustee (${text(row?.trusteeAssignment)})`;
  if (is(location, "Work Release")) {
    return `Work Release (${text(row?.workReleaseAssignment)})`;
  }
  return getHousing(row);
};
